import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FaGithub, FaLinkedin, FaEnvelope, FaWhatsapp, FaMapMarkerAlt,
  FaYoutube, FaInstagram, FaFacebookF, FaPinterestP,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import BlurFade from '../ui/BlurFade';
import { contact } from '../../data/contact';
import { social, youtubeChannelId } from '../../data/social';

const ICONS = {
  GitHub: FaGithub,
  LinkedIn: FaLinkedin,
  Email: FaEnvelope,
  WhatsApp: FaWhatsapp,
  Location: FaMapMarkerAlt,
};

const SOCIAL_ICONS = {
  LinkedIn: FaLinkedin,
  GitHub: FaGithub,
  YouTube: FaYoutube,
  X: FaXTwitter,
  Instagram: FaInstagram,
  Facebook: FaFacebookF,
  Pinterest: FaPinterestP,
};

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const targetEmail = contact.find((c) => c.label === 'Email')?.value;

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Portfolio contact from ${form.name || 'website visitor'}`);
    const body = encodeURIComponent(
      `${form.message}\n\n— ${form.name}${form.email ? ` (${form.email})` : ''}`
    );
    window.location.href = `mailto:${targetEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <section id="contact" className="bg-white py-20 text-black">
      <div className="mx-auto grid max-w-4xl gap-10 px-6 md:grid-cols-2">
        <div>
          <BlurFade>
            <h2 className="text-3xl font-black leading-tight sm:text-4xl">
              Let's talk for <span className="text-black/30">Something special</span>
            </h2>
          </BlurFade>
          <BlurFade delay={0.1}>
            <p className="mt-4 max-w-sm text-black/60">
              Have a project in mind or just want to connect? Send a message and I'll get back to you.
            </p>
          </BlurFade>

          <div className="mt-8 flex flex-col gap-3">
            {contact.map((c, i) => {
              const Icon = ICONS[c.label];
              return (
                <BlurFade key={c.label} delay={0.15 + i * 0.05}>
                  {c.link ? (
                    <a
                      href={c.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm font-medium text-black/70 hover:text-black"
                    >
                      {Icon && <Icon size={14} />} {c.value}
                    </a>
                  ) : (
                    <span className="flex items-center gap-3 text-sm font-medium text-black/70">
                      {Icon && <Icon size={14} />} {c.value}
                    </span>
                  )}
                </BlurFade>
              );
            })}
          </div>
        </div>

        <BlurFade delay={0.1}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your name"
              required
              className="rounded-lg border-2 border-black px-4 py-2.5 text-sm outline-none placeholder:text-black/40"
            />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email"
              required
              className="rounded-lg border-2 border-black px-4 py-2.5 text-sm outline-none placeholder:text-black/40"
            />
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="How can I help?"
              required
              rows={5}
              className="resize-none rounded-lg border-2 border-black px-4 py-2.5 text-sm outline-none placeholder:text-black/40"
            />
            <motion.button
              type="submit"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="mt-1 rounded-full bg-black px-6 py-2.5 text-sm font-bold text-white"
            >
              Get In Touch
            </motion.button>
          </form>
        </BlurFade>
      </div>

      <div className="mx-auto mt-16 max-w-4xl px-6">
        <BlurFade>
          <h3 className="mb-2 text-xl font-black">
            Follow <span className="text-black/30">my work</span>
          </h3>
        </BlurFade>
        <BlurFade delay={0.05}>
          <p className="mb-6 text-sm text-black/60">Where I post, build, and share in public.</p>
        </BlurFade>

        <BlurFade delay={0.1}>
          <div className="mb-8 flex flex-wrap gap-3">
            {social.map((s) => {
              const Icon = SOCIAL_ICONS[s.label];
              return (
                <a
                  key={s.label}
                  href={s.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-black text-black transition-colors hover:bg-black hover:text-white"
                >
                  {Icon && <Icon size={16} />}
                </a>
              );
            })}
          </div>
        </BlurFade>

        <BlurFade delay={0.15}>
          <div className="overflow-hidden rounded-xl border-2 border-black">
            <iframe
              className="aspect-video w-full"
              src={`https://www.youtube.com/embed/videoseries?list=UU${youtubeChannelId.slice(2)}`}
              title="Latest videos from Prathamesh Shete"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default Contact;
